import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import loginIllustration from '../../../assets/images/login.png';
import amberLogo from '../../assets/images/amber-logo.png';
import TwoFactorPrompt from './components/TwoFactorPrompt';
import FirstLoginSetup from './components/FirstLoginSetup';

/* ─── Inject animation keyframes once ─── */
const STYLE_ID = 'login-beam-styles';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ── Blur orb float ── */
    @keyframes orbFloat1 {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      33%       { transform: translate(30px, -20px) scale(1.04); }
      66%       { transform: translate(-20px, 15px) scale(0.97); }
    }
    @keyframes orbFloat2 {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      40%       { transform: translate(-25px, 20px) scale(1.06); }
      70%       { transform: translate(20px, -25px) scale(0.95); }
    }
    @keyframes orbFloat3 {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      50%       { transform: translate(15px, 30px) scale(1.05); }
    }

    /* ── Conic border glow for button ── */
    @keyframes conicSpin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }

    /* ── Entrance animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .la-0 { animation: fadeIn  0.5s ease both 0ms; }
    .la-1 { animation: fadeUp  0.5s ease both 60ms; }
    .la-2 { animation: fadeUp  0.5s ease both 140ms; }
    .la-3 { animation: fadeUp  0.5s ease both 210ms; }
    .la-4 { animation: fadeUp  0.5s ease both 280ms; }
    .la-5 { animation: fadeUp  0.5s ease both 350ms; }
    .la-6 { animation: fadeUp  0.5s ease both 420ms; }
    .la-7 { animation: fadeUp  0.5s ease both 490ms; }
    .la-f { animation: fadeIn  0.6s ease both 560ms; }

    /* Input focus ring */
    .login-input:focus {
      border-color: rgba(139,92,246,0.6) !important;
      box-shadow: 0 0 0 3px rgba(139,92,246,0.14), 0 0 0 1px rgba(236,72,153,0.08) !important;
      background-color: #ffffff !important;
      outline: none;
    }

    /* Button hover */
    .login-cta:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 28px rgba(79,70,229,0.42), 0 0 0 1px rgba(139,92,246,0.3) !important;
    }
    .login-cta:active:not(:disabled) { transform: translateY(0); }
    .login-cta { transition: transform 0.15s ease, box-shadow 0.18s ease; }

    /* Right panel entrance */
    .rp-anim { animation: fadeIn 0.7s ease both 100ms; }
  `;
  document.head.appendChild(style);
}

const CompanyUserLogin = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Super-admin login can branch: 'password' (default), 'twofa' (2FA enabled), 'setup' (first login).
  const [stage, setStage] = useState('password');
  const [pendingToken, setPendingToken] = useState(null);
  const [setupToken, setSetupToken] = useState(null);

  // Only auto-redirect from the password stage. During 'setup', the account becomes authenticated
  // after the password reset but we intentionally stay to offer the optional 2FA step.
  useEffect(() => { if (!loading && isAuthenticated && stage === 'password') navigate('/'); }, [navigate, isAuthenticated, loading, stage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email address';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 6) e.password = 'Minimum 6 characters';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || !validate()) return;
    setErrors({});
    setIsLoading(true);
    try {
      const res = await login(formData.email, formData.password);
      if (res.status === 'session') {
        formData.rememberMe ? localStorage.setItem('rememberMe', 'true') : localStorage.removeItem('rememberMe');
        navigate('/');
      } else if (res.status === 'twofa_required') {
        setPendingToken(res.pendingToken);
        setStage('twofa');
      } else if (res.status === 'setup_required') {
        setSetupToken(res.setupToken);
        setStage('setup');
      } else {
        setErrors({ general: res.error || 'Invalid credentials. Please try again.' });
      }
    } catch (err) {
      setErrors({ general: err.message || 'Unexpected error.' });
    } finally { setIsLoading(false); }
  };

  const backToLogin = () => {
    setStage('password');
    setPendingToken(null);
    setSetupToken(null);
    setErrors({});
    setFormData((p) => ({ ...p, password: '' }));
  };

  return (
    <div style={S.page}>

      {/* ════════════ LEFT PANEL ════════════ */}
      <div style={S.leftPanel}>

        {/* ── Blur orbs (background layer) ── */}
        <div style={S.orbsLayer}>
          <div style={S.orb1} />
          <div style={S.orb2} />
          <div style={S.orb3} />
          <div style={S.orb4} />
        </div>

        {/* ── Frosted-glass inner card ── */}
        <div style={S.glassCard}>

          {/* Logo */}
          <div style={S.logoRow} className="la-0">
            <img src={amberLogo} alt="Amber Enterprises" style={S.logoImg} />
          </div>

          {/* System badge */}
          <div className="la-1" style={S.badgeWrap}>
            <span style={S.badge}>
              <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                <path d="M10 2L3 5.5V10C3 14.08 6.08 17.88 10 19C13.92 17.88 17 14.08 17 10V5.5L10 2Z"
                  fill="rgba(139,92,246,0.18)" stroke="#16B8AA" strokeWidth="1.5" strokeLinejoin="round" />
                <circle cx="10" cy="10" r="2.5" fill="#16B8AA" />
              </svg>
              ASOS &nbsp;·&nbsp; Vendor Management System
            </span>
          </div>

          {/* ── Centred content block ── */}
          <div style={S.centreBlock}>

            {stage === 'twofa' && (
              <TwoFactorPrompt
                pendingToken={pendingToken}
                onSuccess={() => navigate('/')}
                onBack={backToLogin}
              />
            )}
            {stage === 'setup' && (
              <FirstLoginSetup
                email={formData.email}
                setupToken={setupToken}
                onComplete={() => navigate('/')}
              />
            )}
            {stage === 'password' && (<>
            {/* Heading */}
            <div className="la-2" style={{ marginBottom: '6px' }}>
              <h1 style={S.heading}>Welcome Back</h1>
            </div>
            <p className="la-3" style={S.subHeading}>
              Sign in to manage vendors, contracts and approvals.
            </p>

            {/* Thin rule */}
            <div className="la-3" style={S.rule} />

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
              {errors.general && (
                <div style={S.errBanner} className="la-3">
                  <Icon name="AlertCircle" size={14} color="#DC2626" />
                  {errors.general}
                </div>
              )}

              {/* Email */}
              <div style={S.field} className="la-4">
                <label style={S.label} htmlFor="l-email">Email Address</label>
                <input
                  id="l-email" name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="you@amberenterprises.com"
                  style={{ ...S.input, ...(errors.email ? S.inputErr : {}) }}
                  className="login-input"
                  autoComplete="username" disabled={isLoading}
                />
                {errors.email && <span style={S.ferr}>{errors.email}</span>}
              </div>

              {/* Password */}
              <div style={S.field} className="la-5">
                <label style={S.label} htmlFor="l-pass">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="l-pass" name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange}
                    placeholder="Enter your password"
                    style={{ ...S.input, paddingRight: '52px', ...(errors.password ? S.inputErr : {}) }}
                    className="login-input"
                    autoComplete="current-password" disabled={isLoading}
                  />
                  <button type="button" style={S.eyeBtn}
                    onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={19} color="#94A3B8" />
                  </button>
                </div>
                {errors.password && <span style={S.ferr}>{errors.password}</span>}
              </div>

              {/* Remember + Forgot */}
              <div style={S.remRow} className="la-6">
                <label style={S.chkLabel}>
                  <input type="checkbox" name="rememberMe" checked={formData.rememberMe}
                    onChange={handleChange} style={S.chk} disabled={isLoading} />
                  Remember me
                </label>
                <button type="button" style={S.forgotBtn}
                  onClick={() => navigate('/forgot-password')}>
                  Forgot password?
                </button>
              </div>

              {/* CTA button with gradient glow */}
              <div style={S.ctaWrap} className="la-7">
                <button type="submit"
                  style={S.cta} className="login-cta" disabled={isLoading}>
                  {isLoading ? (
                    <span style={S.spinRow}>
                      <span style={S.spinner} /> Signing in…
                    </span>
                  ) : 'Log In'}
                </button>
              </div>
            </form>
            </>)}

          </div>{/* end centreBlock */}

          {/* Footer */}
          <div style={S.footer} className="la-f">
            <span>© 2025 Amber Enterprises India Limited</span>
            <a href="#" style={S.footerLink}>Privacy Policy</a>
          </div>
        </div>
      </div>

      {/* ════════════ RIGHT PANEL ════════════ */}
      <div style={S.rightPanel} className="rp-anim">
        <div style={S.gridOverlay} />
        <div style={S.rightContent}>
          <div style={S.rightPill}>Vendor Management</div>
          <h2 style={S.rightHeading}>
            Effortlessly manage your vendors and operations.
          </h2>
          <p style={S.rightSubtext}>
            Log in to access your Vendor Management dashboard and keep your supply chain on track.
          </p>
          <div style={S.dashWrap}>
            <img src={loginIllustration}
              alt="Vendor Management Illustration" style={S.dashImg} />
          </div>
        </div>
      </div>

    </div>
  );
};

/* ─── Design constants ─── */
const INDIGO = '#0A9188';

const S = {
  /* ── Page ── */
  page: {
    display: 'flex',
    width: '100%', height: '100%', minHeight: '100%',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /* ── Left panel ── */
  leftPanel: {
    flex: '0 0 46%', width: '46%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  },

  /* ── Blur orbs ── */
  orbsLayer: {
    position: 'absolute', inset: 0, zIndex: 0,
    pointerEvents: 'none',
  },
  orb1: {
    position: 'absolute',
    top: '-120px', left: '-80px',
    width: '420px', height: '420px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(236,72,153,0.18) 0%, rgba(167,139,250,0.10) 50%, transparent 75%)',
    filter: 'blur(40px)',
    animation: 'orbFloat1 9s ease-in-out infinite',
  },
  orb2: {
    position: 'absolute',
    bottom: '-100px', right: '-60px',
    width: '380px', height: '380px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.20) 0%, rgba(236,72,153,0.08) 55%, transparent 78%)',
    filter: 'blur(45px)',
    animation: 'orbFloat2 11s ease-in-out infinite',
  },
  orb3: {
    position: 'absolute',
    top: '38%', left: '55%',
    width: '260px', height: '260px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 70%)',
    filter: 'blur(35px)',
    animation: 'orbFloat3 13s ease-in-out infinite',
  },
  orb4: {
    position: 'absolute',
    bottom: '15%', left: '-40px',
    width: '220px', height: '220px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 70%)',
    filter: 'blur(30px)',
    animation: 'orbFloat1 15s 2s ease-in-out infinite',
  },

  /* ── Frosted glass card ── */
  glassCard: {
    position: 'relative', zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '32px 56px 28px',
    boxSizing: 'border-box',
  },

  /* Vertically-centred main block */
  centreBlock: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: '420px',
    width: '100%',
    alignSelf: 'center',
  },

  /* Logo */
  logoRow: {
    display: 'flex', alignItems: 'center',
    flexShrink: 0,
    marginBottom: '14px',
  },
  logoImg: { height: '44px', width: 'auto', objectFit: 'contain' },

  /* Badge */
  badgeWrap: { flexShrink: 0, marginBottom: '0' },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    padding: '5px 13px',
    borderRadius: '999px',
    border: '1.5px solid rgba(139,92,246,0.3)',
    background: 'rgba(139,92,246,0.07)',
    backdropFilter: 'blur(6px)',
    fontSize: '11px', fontWeight: '700',
    color: '#0FA89B',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },

  /* Heading block — centred in remaining space */
  heading: {
    fontSize: '34px', fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 8px 0',
    letterSpacing: '-0.7px', lineHeight: '1.15',
  },
  subHeading: {
    fontSize: '14.5px', color: '#64748B',
    margin: '0 0 0 0', lineHeight: '1.6',
  },

  rule: {
    height: '1px',
    background: 'linear-gradient(90deg, rgba(139,92,246,0.2), rgba(236,72,153,0.15), transparent)',
    margin: '20px 0 22px',
  },

  /* Fields */
  field: { display: 'flex', flexDirection: 'column', marginBottom: '18px' },
  label: {
    fontSize: '13px', fontWeight: '600', color: '#334155',
    marginBottom: '8px', letterSpacing: '0.05px',
  },
  input: {
    width: '100%',
    padding: '15px 17px',
    fontSize: '15px',
    color: '#0F172A',
    backgroundColor: 'rgba(255,255,255,0.75)',
    border: '1.5px solid rgba(203,213,225,0.8)',
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backdropFilter: 'blur(6px)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  inputErr: { borderColor: '#F87171', backgroundColor: 'rgba(255,241,242,0.8)' },
  ferr: { fontSize: '12px', color: '#EF4444', marginTop: '5px' },
  eyeBtn: {
    position: 'absolute', right: '14px', top: '50%',
    transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '2px', display: 'flex', alignItems: 'center',
  },

  /* Remember row */
  remRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '22px', marginTop: '2px',
  },
  chkLabel: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '14px', color: '#475569',
    cursor: 'pointer', userSelect: 'none',
  },
  chk: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#0FA89B' },
  forgotBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: '#0FA89B', fontWeight: '600', padding: 0,
    fontFamily: 'inherit',
  },

  /* ── CTA ── */
  ctaWrap: { position: 'relative', borderRadius: '13px' },
  cta: {
    width: '100%',
    padding: '15px',
    fontSize: '16px', fontWeight: '700',
    color: '#ffffff',
    background: `linear-gradient(135deg, #0A9188 0%, #0FA89B 100%)`,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: '0.3px',
    boxShadow: '0 0 0 2px rgba(139,92,246,0.5), 0 4px 20px rgba(79,70,229,0.35)',
    display: 'block',
    position: 'relative',
    zIndex: 1,
  },
  errBanner: {
    display: 'flex', alignItems: 'center', gap: '8px',
    backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5',
    color: '#DC2626', borderRadius: '10px',
    padding: '11px 14px', fontSize: '13px', marginBottom: '16px',
  },
  spinRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '9px',
  },
  spinner: {
    width: '17px', height: '17px',
    border: '2.5px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },

  /* Footer */
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '11.5px', color: '#94A3B8',
    marginTop: 'auto', paddingTop: '14px',
  },
  footerLink: { color: '#94A3B8', textDecoration: 'none' },

  /* ── Right panel ── */
  rightPanel: {
    flex: '0 0 54%', width: '54%',
    background: 'linear-gradient(140deg, #087F78 0%, #0A9188 40%, #0A6D66 100%)',
    position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  gridOverlay: {
    position: 'absolute', inset: 0,
    backgroundImage: `linear-gradient(rgba(255,255,255,0.033) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.033) 1px, transparent 1px)`,
    backgroundSize: '40px 40px', pointerEvents: 'none',
  },
  rightContent: {
    position: 'relative', zIndex: 1,
    padding: '40px 44px', width: '100%', maxWidth: '600px',
    display: 'flex', flexDirection: 'column', gap: '14px',
    boxSizing: 'border-box',
  },
  rightPill: {
    display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center',
    padding: '4px 14px', borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.22)',
    fontSize: '11.5px', fontWeight: '600',
    color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  rightHeading: {
    fontSize: '30px', fontWeight: '700', color: '#ffffff',
    lineHeight: '1.32', margin: 0, letterSpacing: '-0.4px',
  },
  rightSubtext: {
    fontSize: '14px', color: 'rgba(255,255,255,0.68)',
    lineHeight: '1.65', margin: 0,
  },
  dashWrap: {
    marginTop: '18px', borderRadius: '12px', overflow: 'hidden',
    boxShadow: '0 20px 50px rgba(0,0,0,0.4), 0 6px 16px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.12)', width: '100%',
  },
  dashImg: { width: '100%', height: 'auto', display: 'block' },
};

export default CompanyUserLogin;